import { useEffect } from 'react';
import { distinctUntilChanged, filter, map } from 'rxjs/operators';
import { AuditLogTags } from '@oolio-group/audit-sdk';
import { networkStatusSubject } from '../hooks/connectivity/useNetworkListener';
import { cartActiveScreenObservable } from '../hooks/useCartNavigationHelper/useCartSwitcherEffect';
import { logCdsEvent } from './cdsAudit';

/**
 * Audit for the two continuously-observed states in CDS.
 *
 * Both are deliberately driven off their observable with distinctUntilChanged
 * rather than from the code that produces them: the cart switcher re-evaluates
 * (and re-navigates) on a 1s global interval, so logging at the navigation
 * helpers would record several thousand lines an hour for a screen nobody
 * touched. Screen changes stay at debug so they are dropped entirely in release
 * builds, where minLogLevel is 'info'.
 */
export const useCdsReactiveAudit = (): void => {
  useEffect(() => {
    const subscription = networkStatusSubject
      .pipe(
        filter((isConnected): isConnected is boolean => isConnected !== null),
        distinctUntilChanged(),
      )
      .subscribe(isConnected => {
        logCdsEvent({
          actionType: isConnected ? 'NETWORK_ONLINE' : 'NETWORK_OFFLINE',
          tag: AuditLogTags.DEVICE,
          phrase: isConnected ? 'internet available' : 'internet lost',
          level: isConnected ? 'info' : 'warn',
        });
      });

    return () => subscription.unsubscribe();
  }, []);

  useEffect(() => {
    const subscription = cartActiveScreenObservable
      .pipe(
        map(route => ({
          screen: route.screen,
          awaiting: route.param?.awaiting,
          error: route.param?.error,
        })),
        distinctUntilChanged(
          (a, b) =>
            a.screen === b.screen &&
            a.awaiting === b.awaiting &&
            a.error === b.error,
        ),
      )
      .subscribe(({ screen, awaiting, error }) => {
        logCdsEvent({
          actionType: 'SCREEN_CHANGED',
          tag: AuditLogTags.USER_ACTION,
          phrase: `screen changed to ${screen}`,
          level: 'debug',
          context: {
            screen,
            ...(awaiting !== undefined ? { awaiting } : {}),
            ...(error !== undefined ? { error } : {}),
          },
        });
      });

    return () => subscription.unsubscribe();
  }, []);
};
